import { ApplicationRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FieldsetHarukiModule, MMIconsModule, AlertController } from 'mm-ui';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';

import { AppComponent } from '../../../app.component';
import { Homepage } from '../../../models/homepage';
import { LoggedUser } from '../../../models/loggedUser.service';
import { Page } from '../../../models/page';
import { Workflow, WorkflowContent } from '../../../models/workflow';
import { ContentService } from '../../cms/content/content.service';
import { CreatePageService } from '../../cms/content/create-page/create-page.service';
import { WorkflowService } from '../workflow.service';
import { TranslateService } from '@ngx-translate/core';

/**
 * Núcleo do menu de workflow: usado na sidebar (`viewMode: sidebar`) e embutido na
 * seção Publicação do new-edit layout (`viewMode: embedded`), sem `MenuWorkflowService`/sidebar.
 */
@Component({
  selector: 'menu-workflow-content',
  standalone: true,
  templateUrl: './menu-workflow-content.component.html',
  styleUrls: ['./menu-workflow-content.component.scss'],
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, FieldsetHarukiModule, MMIconsModule]
})
export class MenuWorkflowContentComponent implements OnInit {
  @Input() viewMode: 'sidebar' | 'embedded' = 'sidebar';
  @Input('pageId') pageId: string;
  @Input('homepageId') homepageId: string;
  /** Obrigatório no modo embutido com página (mesma ideia do FAB / `getPage`). */
  @Input('menuId') menuId: string;

  /** Só usado no modo sidebar (fechar o painel do FAB). */
  @Output() closeSidebar = new EventEmitter<void>();

  messageStatus: string;
  messageText: string;
  workflow: Workflow;
  workflows: Workflow[];
  hasPermission: boolean;
  removeWorkflow = false;
  inerithWorkflow: boolean = null;
  workflowId: string;
  type: 'PAGE' | 'HOMEPAGE';

  page: Page;
  homepage: Homepage;

  workflowSelect = new UntypedFormControl('');
  workflowList: { label: string; value: string }[];

  constructor(
    private pageService: CreatePageService,
    private workflowService: WorkflowService,
    private contentService: ContentService,
    private alert: AlertController,
    private user: LoggedUser,
    private appRef: ApplicationRef,
    public router: Router,
    private translate: TranslateService
  ) {
    this.alert.setViewContainerRef((this.appRef.components[0].instance as AppComponent).vcr);
  }

  ngOnInit(): void {
    this.workflow = null;
    this.getWorkflowStatus();
    this.getWorkflowList();
    this.getRouterEvents();
  }

  onCloseClick(): void {
    this.closeSidebar.emit();
  }

  getRouterEvents(): void {
    this.router.events.pipe(throttleTime(1500), filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.getWorkflowStatus();
    });
  }

  getAgreegedWorkflow(): void {
    if (this.workflowId) {
      if (this.workflow) {
        if (this.workflow.id != this.workflowId) {
          this.workflowSelect.setValue(this.workflowId);
          this.getWorkflow(this.workflowId);
        }
      } else {
        this.workflowSelect.setValue(this.workflowId);
        this.getWorkflow(this.workflowId);
      }
    } else {
      this.workflow = null;
    }
  }

  sendWorkflow(): void {
    const rest = this.pageId ? 'pages' : 'homepages';
    const id = this.pageId ? this.pageId : this.homepageId;

    this.workflowService.addWorkflowtoContent(id, this.workflowSelect.value, rest).subscribe(
      () => this.getWorkflow(this.workflowSelect.value),
      () => this.setMessage('danger', this.translate.instant('workflow.addError'))
    );
  }

  getWorkflow(id: string): void {
    const sub = this.workflowService.getWorkflow(id).subscribe((work) => {
      this.workflow = work;
      sub.unsubscribe();
    });
  }

  map(): string {
    return this.workflow?.responsibles?.map((item) => item.profileName).join(', ') ?? '';
  }

  setMessage(status: string, message: string): void {
    this.messageStatus = status;
    this.messageText = message;
  }

  getWorkflowList(): void {
    this.hasPermission = this.user.checkRole('FT');
    if (this.hasPermission) {
      this.workflowService.searchWorkflows({ unpaged: 'true' }).subscribe((workflows) => this.createWorkflowList(workflows));
    }
  }

  createWorkflowList(workflows: WorkflowContent): void {
    this.workflows = workflows.content;
    this.workflowList = workflows.content.map((w) => ({ label: w.title, value: w.id }));
  }

  alertSegregate(): void {
    const config = {
      title: 'Segregar',
      message: this.translate.instant(`workflow.menu.${this.type}.willLoseInerithWorkflow`),
      buttons: [
        {
          text: 'Cancelar',
          type: 'outline-dark me-1',
          handler: () => {}
        },
        {
          text: 'Continuar',
          type: 'primary',
          handler: () => this.segregate()
        }
      ]
    };
    this.alert.configure(config);
    this.alert.open();
  }

  removeWorkSegregate(): void {
    let removeSergregate$: Observable<object>;
    if (this.pageId) {
      removeSergregate$ = this.workflowService.removeSegregateContent(this.pageId, 'page');
    } else {
      removeSergregate$ = this.workflowService.removeSegregateContent(this.homepageId, 'homepage');
    }

    removeSergregate$.subscribe(
      () => {
        this.setMessage('success', this.translate.instant('workflow.segreDeleteSuccess'));
        this.inerithWorkflow = true;
        this.workflow = null;
        this.removeWorkflow = false;
        this.getWorkflowStatus();
      },
      () => this.setMessage('danger', this.translate.instant('workflow.segreDeleteError'))
    );
  }

  segregate(): void {
    let segregate$: Observable<object>;
    if (this.pageId) {
      segregate$ = this.workflowService.segregateContent(this.pageId, 'page');
    } else {
      segregate$ = this.workflowService.segregateContent(this.homepageId, 'homepage');
    }

    segregate$.subscribe(
      () => {
        if (this.pageId) {
          this.page.inheritWorkflow = false;
          this.workflow = undefined;
        } else {
          this.homepage.inheritWorkflow = false;
          this.workflow = undefined;
        }
        this.inerithWorkflow = false;
        this.workflowId = null;
        this.getWorkflowStatus();
      },
      () => this.setMessage('danger', 'Não foi possível segregar o')
    );
  }

  getWorkflowStatus(): void {
    if (this.pageId) {
      this.type = 'PAGE';
      if (this.viewMode === 'embedded') {
        if (!this.menuId) {
          return;
        }
        this.pageService.getPage(this.pageId, this.menuId).subscribe(
          (page) => {
            this.page = page;
            this.inerithWorkflow = this.page.inheritWorkflow;
            this.workflowId = this.page.workflowId;
            this.getAgreegedWorkflow();
          },
          () => this.setMessage('danger', this.translate.instant('workflow.addError'))
        );
      } else {
        this.pageService
          .getPageObj()
          .pipe(filter((page) => Object.keys(page).length > 0))
          .subscribe((page) => {
            this.page = page;
            this.inerithWorkflow = this.page.inheritWorkflow;
            this.workflowId = this.page.workflowId;
            this.getAgreegedWorkflow();
          });
      }
    } else {
      this.type = 'HOMEPAGE';
      if (this.viewMode === 'embedded' && this.homepageId) {
        this.contentService.getHomepageByIdEdit(this.homepageId).subscribe(
          (hp) => {
            this.homepage = hp;
            this.workflowId = this.homepage.workflowId;
            if (this.workflowId) {
              this.inerithWorkflow = false;
            } else {
              this.inerithWorkflow = true;
            }
            this.getAgreegedWorkflow();
          },
          () => {}
        );
      } else {
        this.homepage = this.contentService.homepageDefinition;
        this.workflowId = this.homepage?.workflowId;
        if (this.workflowId) {
          this.inerithWorkflow = false;
        } else {
          this.inerithWorkflow = true;
        }
        this.getAgreegedWorkflow();
      }
    }
  }
}
